import React, { useState, useEffect } from 'react';
import { Spinner } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../services/axios';
import { formatAppDate } from '../../utils/dateFormat';
import { formatJdPreviewHtml, JD_PREVIEW_INNER_CSS } from '../../utils/jdPreviewFormat';
import { formatPositionStatusLabel } from '../../utils/positionStatus';
import GlobalPagination from '../../components/ui/GlobalPagination';

const Database = ({ embedded = false }) => {
  const navigate = useNavigate();
  const { positionId: urlPositionId } = useParams();

  // ── List state ─────────────────────────────────────────────────────────────
  const [loading, setLoading]       = useState(true);
  const [positions, setPositions]   = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [listPage, setListPage]     = useState(1);
  const [listPageSize, setListPageSize] = useState(10);

  // ── Detail state ───────────────────────────────────────────────────────────
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [jdContent, setJdContent]               = useState('');
  const [resumes, setResumes]                   = useState([]);
  const [rankings, setRankings]                 = useState([]);
  const [detailLoading, setDetailLoading]       = useState(false);
  const [resumePage, setResumePage]             = useState(1);
  const [resumePageSize, setResumePageSize]     = useState(10);

  useEffect(() => { fetchPositions(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-load detail when navigated to /database/:positionId
  useEffect(() => {
    if (!urlPositionId) return;
    const id = Number(urlPositionId);
    if (isNaN(id)) return;
    // Try to find in already-loaded list first; otherwise build a stub and fetch data
    const found = positions.find(p => p.positionId === id);
    if (found) {
      setSelectedPosition(found);
      setResumePage(1);
      fetchPositionData(id);
    } else {
      // positions not yet loaded — wait for them (the other effect handles fetchPositions)
      // set a placeholder so detail view shows immediately once positions arrive
      setSelectedPosition({ positionId: id, positionName: '…', createdAt: new Date().toISOString() });
      setResumePage(1);
      fetchPositionData(id);
    }
  }, [urlPositionId, positions.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPositions = async () => {
    try {
      const resp = await api.get('/positions');
      setPositions(resp.data || []);
    } catch {
      toast.error('Failed to load positions');
    } finally {
      setLoading(false);
    }
  };

  const fetchPositionData = async (positionId) => {
    setDetailLoading(true);
    try {
      const [resumesResp, posResp] = await Promise.all([
        api.get(`/resumes/position/${positionId}`),
        api.get(`/positions/${positionId}`).catch(() => null),
      ]);
      setResumes(resumesResp.data || []);
      if (posResp?.data) {
        setSelectedPosition((prev) => ({ ...(prev || {}), ...posResp.data }));
      }

      // JD fetch
      try {
        const jdResp = await api.get(`/job-descriptions/position/${positionId}`);
        const jds = jdResp.data;
        const first = Array.isArray(jds) ? jds[0] : jds;
        setJdContent(first?.jdText || first?.content || first?.jobDescriptionText || '');
      } catch { setJdContent(''); }

      // Rankings fetch
      try {
        const rankResp = await api.get(`/stack-ranking/position/${positionId}/results`);
        const results = rankResp.data || [];
        setRankings(results.map((r, idx) => ({
          rank: r.rank || idx + 1,
          name: r.candidateName || 'Unknown',
          email: r.email || '',
          mobile: r.mobile || '',
          total_score: r.totalScore || 0,
          resume_id: r.resumeId,
        })));
      } catch { setRankings([]); }
    } catch {
      toast.error('Failed to load position data');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleSelect = (pos) => {
    setSelectedPosition(pos);
    setResumePage(1);
    fetchPositionData(pos.positionId);
  };

  const handleBack = () => {
    navigate('/open-positions');
  };

  // ── List pagination ────────────────────────────────────────────────────────
  const filtered = searchTerm.trim()
    ? positions.filter(p => {
        const q = searchTerm.toLowerCase();
        return (p.positionName || '').toLowerCase().includes(q) || (p.jobCode || '').toLowerCase().includes(q);
      })
    : positions;
  const listTotal      = filtered.length;
  const listFirstIdx   = (listPage - 1) * listPageSize;
  const listLastIdx    = listFirstIdx + listPageSize;
  const listRecords    = filtered.slice(listFirstIdx, listLastIdx);
  const listTotalPages = Math.ceil(listTotal / listPageSize);

  // ── Resume pagination ──────────────────────────────────────────────────────
  const resumeFirstIdx   = (resumePage - 1) * resumePageSize;
  const resumeLastIdx    = resumeFirstIdx + resumePageSize;
  const resumeRecords    = resumes.slice(resumeFirstIdx, resumeLastIdx);
  const resumeTotalPages = Math.ceil(resumes.length / resumePageSize);

  const FONT = "'Plus Jakarta Sans','Inter',sans-serif";

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) return (
    <div style={{ textAlign: 'center', padding: '60px 20px', fontFamily: FONT }}>
      <Spinner animation="border" style={{ color: '#0041C2' }} />
      <p style={{ marginTop: 14, color: 'var(--ds-muted)', fontSize: 13 }}>Loading database…</p>
    </div>
  );

  // ════════════════════════════════════════════════════════════════════════════
  // DETAIL VIEW
  // ════════════════════════════════════════════════════════════════════════════
  if (selectedPosition) return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', fontFamily: FONT, overflow: 'hidden' }}>

      {/* Detail header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14,
        padding: '12px 24px', borderBottom: '1px solid var(--ds-border)',
        background: 'var(--ds-card)', flexShrink: 0,
      }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--ds-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {selectedPosition.positionName}
          </div>
          <div style={{ fontSize: 11, color: 'var(--ds-muted)', marginTop: 1 }}>
            {[selectedPosition.jobCode, selectedPosition.clientName].filter(Boolean).join(' · ')}
          </div>
        </div>
        <button
          onClick={handleBack}
          style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px',
            borderRadius: 8, border: '1.5px solid var(--ds-border)', background: 'transparent',
            color: 'var(--ds-text2)', fontSize: 12, fontWeight: 600, cursor: 'pointer',
            fontFamily: FONT, transition: 'all 0.18s', flexShrink: 0,
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = '#1A5FD4'; e.currentTarget.style.color = '#1A5FD4'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = ''; e.currentTarget.style.color = ''; }}
        >
          ← Back
        </button>
      </div>

      {/* Detail body */}
      {detailLoading ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Spinner animation="border" style={{ color: '#0041C2' }} />
        </div>
      ) : (
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

          {/* ── Left 2/5 — position info + JD ─────────────────────────────── */}
          <div style={{
            width: '40%', borderRight: '1px solid var(--ds-border)',
            overflowY: 'auto', padding: '20px 20px',
          }}>
            {/* Info card */}
            <div style={{
              background: 'var(--ds-surface)', border: '1.5px solid var(--ds-border)',
              borderRadius: 12, padding: '14px 18px', marginBottom: 16, flexShrink: 0,
            }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--ds-muted)', textTransform: 'uppercase', letterSpacing: '0.09em', marginBottom: 12 }}>
                Position Details
              </div>
              {[
                { label: 'Position',   value: selectedPosition.positionName },
                { label: 'Client',     value: selectedPosition.clientName || '—' },
                { label: 'Job Code',   value: selectedPosition.jobCode || '—' },
                { label: 'Status',     value: formatPositionStatusLabel(selectedPosition) },
                { label: 'Created By', value: selectedPosition.createdBy || '—' },
                { label: 'Created',    value: formatAppDate(selectedPosition.createdAt) },
              ].map((row, i, arr) => (
                <div key={row.label} style={{
                  display: 'flex', gap: 12, padding: '8px 0',
                  borderBottom: i < arr.length - 1 ? '1px solid var(--ds-border)' : 'none',
                }}>
                  <div style={{ fontSize: 11, color: 'var(--ds-text2)', width: 88, flexShrink: 0, fontWeight: 500 }}>{row.label}</div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ds-text)', wordBreak: 'break-word' }}>
                    {row.label === 'Job Code' && row.value !== '—' ? (
                      <span style={{ background: '#0041C2', color: '#fff', borderRadius: 6, padding: '2px 9px', fontSize: 11, fontWeight: 700, fontFamily: 'monospace' }}>{row.value}</span>
                    ) : row.label === 'Status' ? (
                      <span style={{
                        background: String(row.value).toLowerCase().startsWith('closed') ? 'rgba(245,158,11,0.14)' : 'rgba(16,185,129,0.12)',
                        color: String(row.value).toLowerCase().startsWith('closed') ? '#D97706' : '#10B981',
                        borderRadius: 20, padding: '2px 10px', fontSize: 11, fontWeight: 700,
                        whiteSpace: 'normal', lineHeight: 1.35,
                      }}>{row.value}</span>
                    ) : row.value}
                  </div>
                </div>
              ))}
            </div>

            {/* JD — fixed-height scrollable slider */}
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--ds-muted)', textTransform: 'uppercase', letterSpacing: '0.09em', marginBottom: 10 }}>
                Job Description
              </div>
              {jdContent ? (
                <>
                  <style>{JD_PREVIEW_INNER_CSS}</style>
                  <div
                    className="jd-preview-content"
                    style={{
                      height: 'calc(100vh - 440px)', minHeight: 180, overflowY: 'auto',
                      background: 'var(--ds-input-bg)', border: '1.5px solid var(--ds-border)',
                      borderRadius: 10, padding: '14px 16px', fontSize: 11.5,
                      color: 'var(--ds-text)', lineHeight: 1.55, textAlign: 'left',
                    }}
                    dangerouslySetInnerHTML={{ __html: formatJdPreviewHtml(jdContent) }}
                  />
                </>
              ) : (
                <div style={{
                  height: 'calc(100vh - 440px)', minHeight: 180,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  border: '1.5px dashed var(--ds-border)', borderRadius: 10,
                  color: 'var(--ds-muted)', fontSize: 12, textAlign: 'center', padding: '28px 16px',
                }}>
                  No job description attached.
                </div>
              )}
            </div>
          </div>

          {/* ── Right 3/5 — resumes ───────────────────────────────────────── */}
          <div style={{
            width: '60%', overflowY: 'auto', padding: '20px 20px',
            display: 'flex', flexDirection: 'column',
          }}>
            {/* Section title */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexShrink: 0 }}>
              <span style={{ fontSize: 13, fontWeight: 800, color: 'var(--ds-text)' }}>Resumes</span>
              {resumes.length > 0 && (
                <span style={{
                  background: '#0041C2', color: '#fff', borderRadius: 20,
                  padding: '2px 10px', fontSize: 11, fontWeight: 700,
                }}>{resumes.length}</span>
              )}
            </div>

            {resumes.length === 0 ? (
              /* Empty state — button centered */
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14 }}>
                <div style={{
                  width: 56, height: 56, borderRadius: '50%',
                  background: 'rgba(0,65,194,0.08)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#0041C2" strokeWidth="2" strokeLinecap="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/><line x1="12" y1="11" x2="12" y2="17"/><line x1="9" y1="14" x2="15" y2="14"/>
                  </svg>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--ds-text)', marginBottom: 4 }}>No Resumes Yet</div>
                  <div style={{ fontSize: 12, color: 'var(--ds-muted)' }}>No resumes have been added for this position.</div>
                </div>
                <button
                  onClick={() => navigate(`/stack-ranking/${selectedPosition.positionId}`)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '10px 26px', borderRadius: 9, border: 'none',
                    background: 'linear-gradient(135deg,#0041C2,#1A5FD4)', color: '#fff',
                    fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: FONT,
                    boxShadow: '0 0 18px rgba(0,65,194,0.28)',
                  }}
                >
                  + Add Resumes
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                {/* Table */}
                <div style={{ overflowX: 'auto', flex: 1 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr style={{ background: 'var(--ds-surface)' }}>
                        {['#', 'Candidate', 'Email', 'Mobile', 'File', 'Added', 'Score'].map(h => (
                          <th key={h} style={{
                            padding: '9px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700,
                            color: 'var(--ds-text2)', textTransform: 'uppercase', letterSpacing: '0.06em',
                            borderBottom: '2px solid var(--ds-border)', whiteSpace: 'nowrap',
                          }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {resumeRecords.map((resume, idx) => {
                        const ranking = rankings.find(r => r.resume_id === resume.resumeId || r.resumeId === resume.resumeId);
                        const rawScore = ranking ? (ranking.total_score ?? ranking.totalScore ?? 0) : null;
                        const score = rawScore !== null ? Math.round(Number(rawScore)) : null;
                        return (
                          <tr
                            key={resume.resumeId}
                            style={{ borderBottom: '1px solid var(--ds-border)', transition: 'background 0.15s' }}
                            onMouseEnter={e => e.currentTarget.style.background = 'var(--ds-hover)'}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                          >
                            <td style={{ padding: '9px 12px', color: 'var(--ds-muted)', fontWeight: 600 }}>
                              {resumeFirstIdx + idx + 1}
                            </td>
                            <td style={{ padding: '9px 12px', fontWeight: 700, color: 'var(--ds-text)', whiteSpace: 'nowrap' }}>
                              {ranking?.name || ranking?.candidateName || resume.candidateName || '—'}
                            </td>
                            <td style={{ padding: '9px 12px', color: 'var(--ds-text2)' }}>
                              {ranking?.email || resume.email || '—'}
                            </td>
                            <td style={{ padding: '9px 12px', color: 'var(--ds-text2)', whiteSpace: 'nowrap' }}>
                              {ranking?.mobile || resume.mobile || '—'}
                            </td>
                            <td style={{ padding: '9px 12px' }}>
                              <span style={{
                                background: 'rgba(0,65,194,0.1)', color: '#0041C2',
                                borderRadius: 20, padding: '2px 8px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                              }}>{resume.fileType || 'PDF'}</span>
                            </td>
                            <td style={{ padding: '9px 12px', color: 'var(--ds-text2)', whiteSpace: 'nowrap' }}>
                              {formatAppDate(resume.createdAt)}
                            </td>
                            <td style={{ padding: '9px 12px' }}>
                              {score !== null ? (
                                <span style={{
                                  background: score >= 80 ? 'rgba(16,185,129,0.12)' : score >= 60 ? 'rgba(245,158,11,0.12)' : 'rgba(239,68,68,0.12)',
                                  color: score >= 80 ? '#10B981' : score >= 60 ? '#D97706' : '#EF4444',
                                  borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 700,
                                }}>{score}%</span>
                              ) : (
                                <span style={{ color: 'var(--ds-muted)', fontSize: 11 }}>—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Resume pagination */}
                {resumes.length > resumePageSize && (
                  <div style={{ flexShrink: 0 }}>
                    <GlobalPagination
                      currentPage={resumePage}
                      totalPages={resumeTotalPages}
                      onPageChange={setResumePage}
                      recordsPerPage={resumePageSize}
                      onPageSizeChange={n => { setResumePageSize(n); setResumePage(1); }}
                      totalItems={resumes.length}
                      startIndex={resumeFirstIdx + 1}
                      endIndex={Math.min(resumeLastIdx, resumes.length)}
                      label="resumes"
                      size="small"
                    />
                  </div>
                )}

                {/* Add More Resumes — centered */}
                <div style={{
                  flexShrink: 0, display: 'flex', justifyContent: 'center',
                  paddingTop: 18, marginTop: 10, borderTop: '1px solid var(--ds-border)',
                }}>
                  <button
                    onClick={() => navigate(`/stack-ranking/${selectedPosition.positionId}`)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      padding: '10px 26px', borderRadius: 9,
                      border: '1.5px solid rgba(0,65,194,0.4)', background: 'transparent',
                      color: '#1A5FD4', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                      fontFamily: FONT, transition: 'all 0.18s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,65,194,0.06)'; e.currentTarget.style.borderColor = '#1A5FD4'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'rgba(0,65,194,0.4)'; }}
                  >
                    + Add More Resumes
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  // ════════════════════════════════════════════════════════════════════════════
  // LIST VIEW
  // ════════════════════════════════════════════════════════════════════════════
  return (
    <div style={{ fontFamily: FONT }}>
      {/* List header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--ds-text)' }}>Database</div>
          <div style={{ fontSize: 12, color: 'var(--ds-muted)', marginTop: 2 }}>
            {positions.length} position{positions.length !== 1 ? 's' : ''} · click a row to view details
          </div>
        </div>
        {/* Search */}
        <div style={{ position: 'relative' }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
            style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--ds-muted)', pointerEvents: 'none' }}>
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            type="text" value={searchTerm}
            onChange={e => { setSearchTerm(e.target.value); setListPage(1); }}
            placeholder="Search positions…"
            style={{
              padding: '8px 12px 8px 32px', border: '1.5px solid var(--ds-border)',
              borderRadius: 9, fontSize: 12, outline: 'none', color: 'var(--ds-text)',
              background: 'var(--ds-input-bg)', width: 240, fontFamily: FONT,
            }}
          />
        </div>
      </div>

      {/* Table */}
      {positions.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--ds-text)', marginBottom: 6 }}>No Positions Found</div>
          <div style={{ fontSize: 12, color: 'var(--ds-muted)' }}>No positions have been created yet.</div>
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <div style={{ fontSize: 12, color: 'var(--ds-muted)' }}>No positions match "<strong>{searchTerm}</strong>"</div>
        </div>
      ) : (
        <>
          <div style={{ border: '1.5px solid var(--ds-border)', borderRadius: 12, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: 'var(--ds-surface)' }}>
                  {['Position Name', 'Client', 'Job Code', 'Status', 'Created', 'Created By'].map(h => (
                    <th key={h} style={{
                      padding: '11px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700,
                      color: 'var(--ds-text2)', textTransform: 'uppercase', letterSpacing: '0.06em',
                      borderBottom: '1.5px solid var(--ds-border)', whiteSpace: 'nowrap',
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {listRecords.map(pos => (
                  <tr
                    key={pos.positionId}
                    style={{ borderBottom: '1px solid var(--ds-border)', cursor: 'pointer', transition: 'background 0.15s' }}
                    onClick={() => handleSelect(pos)}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--ds-hover)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <td style={{ padding: '12px 16px', fontWeight: 700, color: 'var(--ds-text)' }}>{pos.positionName}</td>
                    <td style={{ padding: '12px 16px', color: 'var(--ds-text2)' }}>{pos.clientName || '—'}</td>
                    <td style={{ padding: '12px 16px' }}>
                      {pos.jobCode ? (
                        <span style={{
                          background: '#0041C2', color: '#fff', borderRadius: 6,
                          padding: '3px 9px', fontSize: 11, fontWeight: 700, fontFamily: 'monospace',
                        }}>{pos.jobCode}</span>
                      ) : '—'}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{
                        background: formatPositionStatusLabel(pos).toLowerCase().startsWith('closed') ? 'rgba(245,158,11,0.14)' : 'rgba(16,185,129,0.12)',
                        color: formatPositionStatusLabel(pos).toLowerCase().startsWith('closed') ? '#D97706' : '#10B981',
                        borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 700,
                      }}>{formatPositionStatusLabel(pos)}</span>
                    </td>
                    <td style={{ padding: '12px 16px', color: 'var(--ds-text2)', whiteSpace: 'nowrap' }}>
                      {formatAppDate(pos.createdAt)}
                    </td>
                    <td style={{ padding: '12px 16px', color: 'var(--ds-text2)' }}>{pos.createdBy || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <GlobalPagination
            currentPage={listPage}
            totalPages={listTotalPages}
            onPageChange={setListPage}
            recordsPerPage={listPageSize}
            onPageSizeChange={n => { setListPageSize(n); setListPage(1); }}
            totalItems={listTotal}
            startIndex={listFirstIdx + 1}
            endIndex={Math.min(listLastIdx, listTotal)}
            label="positions"
          />
        </>
      )}
    </div>
  );
};

export default Database;
